import { Button, Checkbox } from '@timelyapp/react';
import api from 'api';
import DropdownListItem from 'components/DropdownListItem';
import { RadioButton } from 'components/radio-new/RadioButton';
import RadioGroup from 'components/radio-new/RadioGroup';
import { AgentReportValue, formatAgentReportValue } from 'components/superreports/AgentReportValue';
import config from 'config';
import {
  AGENT_REPORT_COLUMNS,
  AGENT_REPORT_LABELS,
  type AgentReportColumn,
  type AgentReportMetrics,
  isAgentReportColumn,
} from 'constants/agentReporting';
import { WIDGET_AGENTS_DONUT, WIDGET_AGENTS_LIST } from 'constants/widgets';
import { dataQueryFilterRequestFactory } from 'factories/superreports';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  updateWidgetColumns,
  updateWidgetOptions,
  type WidgetWithData,
} from 'redux/modules/superreports.types';
import { getFilteredQueryInApiFormat } from 'redux/selectors/superreports';
import type { ThunkDispatch } from 'types/redux';
import AgentActivityDonut from './AgentActivityDonut';
import styles from './AgentActivityWidget.module.scss';
import { type AgentActivityReport, agentActivityDays } from './agentActivityData';
import { AgentSessionList } from './table/AgentSessionRows';

export function AgentActivityOptions({ widget }: { widget: WidgetWithData }) {
  const dispatch = useDispatch<ThunkDispatch>();
  const reference = widget.id || widget._ref;
  const isTable = widget.widget_type === WIDGET_AGENTS_LIST;
  return (
    <>
      <DropdownListItem type="header">
        {isTable ? 'Session columns' : 'Chart metric'}
      </DropdownListItem>
      <RadioGroup
        className={styles.options}
        selectedValue={widget.options?.agentMetric || 'agent_sessions'}
      >
        {AGENT_REPORT_COLUMNS.filter(column => !isTable || column !== 'agent_sessions').map(
          column => {
            const checked = widget.columns.names.includes(column);
            const change = () =>
              isTable
                ? dispatch(
                    updateWidgetColumns({
                      reference,
                      names: checked
                        ? widget.columns.names.filter(name => name !== column)
                        : [...widget.columns.names, column],
                    })
                  )
                : dispatch(updateWidgetOptions({ reference, options: { agentMetric: column } }));
            return (
              <DropdownListItem key={column} onClick={change}>
                {isTable ? (
                  <Checkbox
                    id={`${reference}-${column}`}
                    checked={checked}
                    onChangeChecked={change}
                    label={AGENT_REPORT_LABELS[column]}
                  />
                ) : (
                  <RadioButton
                    id={`${reference}-${column}`}
                    value={column}
                    onChangeChecked={change}
                    label={AGENT_REPORT_LABELS[column]}
                  />
                )}
              </DropdownListItem>
            );
          }
        )}
      </RadioGroup>
    </>
  );
}

const loadActivity = (
  filters: Record<string, string>,
  signal: AbortSignal
): Promise<AgentActivityReport> =>
  api.post(
    'reports/filter.json',
    { ...filters, scope: 'agent_activity', limit: 1 },
    { abortSignal: signal }
  );

export function AgentActivityChart({
  filters,
  metric,
  load = loadActivity,
  donut = false,
}: {
  filters: Record<string, string>;
  metric: AgentReportColumn;
  load?: typeof loadActivity;
  donut?: boolean;
}) {
  const request = JSON.stringify(filters);
  const [data, setData] = useState<AgentActivityReport>();
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: retry restarts the same request.
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setError(false);
    load(JSON.parse(request), controller.signal)
      .then(value => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
  }, [request, load, retry]);

  if (error)
    return (
      <div className={styles.body} role="alert">
        Could not load agent activity.{' '}
        <Button size="small" onClick={() => setRetry(value => value + 1)}>
          Try again
        </Button>
      </div>
    );
  if (!data)
    return (
      <p className={styles.body} role="status">
        Loading agent activity…
      </p>
    );
  if (!data.totals.agent_periods) return <p className={styles.body}>No matching agent activity.</p>;
  if (donut) return <AgentActivityDonut data={data} metric={metric} />;
  const days = agentActivityDays(data.breakdowns.days, filters.since, filters.until);
  const points = days.map(day => ({ ...day, value: day.metrics[metric] }));
  const label = AGENT_REPORT_LABELS[metric];
  const hasUnknown = points.some(day => day.value === null);
  return (
    <section className={styles.body} aria-label="Agent activity by day">
      <div className={styles.summary}>
        <div>
          <span>{label}</span>
          <strong>
            <AgentReportValue metrics={data.totals} column={metric} link={false} />
          </strong>
        </div>
        <p>
          {metric === 'agent_sessions'
            ? 'Distinct sessions in the selected period. A resumed session can appear on more than one day.'
            : 'Recorded agent activity by day. Missing measurements are excluded.'}
        </p>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={points} margin={{ top: 12, right: 12, bottom: 4, left: 8 }}>
          <CartesianGrid vertical={false} stroke="var(--theme_color_content_divider_subtle)" />
          <XAxis
            dataKey="id"
            tickFormatter={day => day.slice(5)}
            minTickGap={35}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            width={80}
            axisLine={false}
            tickLine={false}
            allowDecimals={metric !== 'agent_sessions' && metric !== 'agent_periods'}
            tickFormatter={value =>
              formatAgentReportValue({ [metric]: value } as AgentReportMetrics, metric)
            }
          />
          <Tooltip
            content={({ active, payload }) => {
              const day = payload?.[0]?.payload;
              return active && day ? (
                <div className={styles.tooltip}>
                  <strong>{day.id}</strong>
                  <p>
                    {label}: {formatAgentReportValue(day.metrics, metric)}
                  </p>
                </div>
              ) : null;
            }}
          />
          <Bar
            dataKey="value"
            name={label}
            fill="var(--theme_color_solid_primary)"
            maxBarSize={44}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      {hasUnknown && <p>Days without measurements are shown as — in the daily values below.</p>}
      <details className={styles.dailyValues}>
        <summary>Daily values and sessions</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">{label}</th>
            </tr>
          </thead>
          <tbody>
            {days.map(day => (
              <tr key={day.id}>
                <th scope="row">{day.id}</th>
                <td>
                  <AgentReportValue metrics={day.metrics} column={metric} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}

export default function AgentActivityWidget({
  widget,
  enabled,
}: {
  widget: WidgetWithData;
  enabled: boolean;
}) {
  const query = useSelector(getFilteredQueryInApiFormat);
  if (config.isPublicReport || !enabled)
    return <p className={styles.body}>Agent activity is available in account reports.</p>;
  const filters = Object.fromEntries(
    Object.entries(dataQueryFilterRequestFactory(query))
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)])
  );
  const key = JSON.stringify(filters);
  if (widget.widget_type === WIDGET_AGENTS_LIST)
    return (
      <AgentSessionList
        key={key}
        filters={filters}
        columns={widget.columns.names
          .filter(isAgentReportColumn)
          .filter(column => column !== 'agent_sessions')}
      />
    );
  return (
    <AgentActivityChart
      key={key}
      filters={filters}
      donut={widget.widget_type === WIDGET_AGENTS_DONUT}
      metric={
        widget.options?.agentMetric && isAgentReportColumn(widget.options.agentMetric)
          ? widget.options.agentMetric
          : 'agent_sessions'
      }
    />
  );
}
